import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons/faLocationDot";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { usePageRenderConfig } from "../DocumentView/PageRenderConfigContext";

interface Props {
  element: DocumentElementType;
}

const staticMapUrl = (lat: number, lng: number, token: string): string =>
  `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+2f6fdb(${lng},${lat})/${lng},${lat},14/1200x400@2x?access_token=${token}`;

/**
 * Read-only render of a page-document location block — a static Mapbox map card
 * with the address and an "Open in Maps" action. Needs a Mapbox token from the
 * host (PageRenderConfig); without one it degrades to an address card + link.
 */
const PageLocationView: React.FC<Props> = ({ element }) => {
  const data = (element.data ?? {}) as {
    address?: string | null;
    lat?: number | null;
    lng?: number | null;
  };
  const { mapboxToken } = usePageRenderConfig();

  const hasLocation =
    typeof data.lat === "number" && typeof data.lng === "number";
  if (!hasLocation) return null;

  const lat = data.lat as number;
  const lng = data.lng as number;
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <figure className="m-0 overflow-hidden rounded-[11px] border border-[#e7e5e1] bg-white">
      {mapboxToken && (
        <a href={mapsHref} target="_blank" rel="noreferrer" className="block">
          <img
            src={staticMapUrl(lat, lng, mapboxToken)}
            alt={data.address ?? "Map"}
            className="h-[200px] w-full object-cover"
          />
        </a>
      )}
      <div className="flex items-center gap-[10px] px-[14px] py-[11px]">
        <FontAwesomeIcon
          icon={faLocationDot}
          className="flex-none text-[14px] text-[#c23a3a]"
        />
        <span className="min-w-0 flex-1 truncate font-jakarta text-[13px] font-semibold text-[#33302b]">
          {data.address ?? `${lat}, ${lng}`}
        </span>
        <a
          href={mapsHref}
          target="_blank"
          rel="noreferrer"
          className="flex flex-none items-center gap-[6px] rounded-[7px] bg-[#f3f1ee] px-[9px] py-[5px] font-jakarta text-[11.5px] font-semibold text-[#6a6660]"
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
          Open in Maps
        </a>
      </div>
    </figure>
  );
};

export default PageLocationView;
